/**
 * Sessions — one row per conversation, and the ledger underneath it.
 *
 * This is where an outlier becomes findable. A blended "$0.017 per session"
 * hides the four-minute call that cost $0.29; sorted by cost per minute, that
 * call is the first row, and one click shows every LLM, speech and carrier
 * event that made it up.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, Download, Phone } from 'lucide-react';
import {
  AdminAPI, UsageFilterState, UsageSessionEvents, UsageSessionRow,
  UsageSessionSort, UsageSessionsResponse,
} from '../../lib/api';
import { Card, Chip, DurationBadge, Empty, MODALITY_LABEL, Table, Td, Th, capitalise, compact, dateTime, duration, int, rate, symbolFor, Money, useCurrency } from './shared';

const PAGE = 50;

// Most recent FIRST. The page opens on "what just happened"; sorted by cost,
// the session someone ran a minute ago sat on page three behind every long
// call of the month and read as missing.
const SORTS: [UsageSessionSort, string][] = [
  ['started', 'Most recent'],
  ['cost', 'Highest cost'],
  ['cost_per_minute', 'Highest cost per minute'],
  ['duration', 'Longest'],
  ['calls', 'Most calls'],
];

// A phone→UI handoff is ONE thread and TWO sessions (one row per leg), so a
// row's identity is the thread plus the channel it ran on.
const rowId = (r: { session_key: string; channel: string | null }) =>
  `${r.session_key}:${r.channel ?? '-'}`;

export const SessionsTab: React.FC<{
  api: AdminAPI;
  filters: UsageFilterState;
  nonce: number;
}> = ({ api, filters, nonce }) => {
  const { symbol } = useCurrency();
  const [sort, setSort] = useState<UsageSessionSort>('started');
  const [includeNonSession, setIncludeNonSession] = useState(false);
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState<UsageSessionsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { setOffset(0); }, [filters, sort, includeNonSession]);

  useEffect(() => {
    setLoading(true);
    api.getUsageSessions(filters, {
      sort, limit: PAGE, offset, include_non_session: includeNonSession,
    })
      .then((r) => { setData(r); setError(null); })
      .catch((e) => setError(`Couldn't load sessions: ${e instanceof Error ? e.message : String(e)}. Refresh to try again.`))
      .finally(() => setLoading(false));
  }, [api, filters, sort, offset, includeNonSession, nonce]);

  const exportCsv = useCallback(async () => {
    setExporting(true);
    try {
      const blob = await api.downloadUsageCsv(filters);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `usage_${filters.from}_${filters.to}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(`Couldn't export the CSV: ${e instanceof Error ? e.message : String(e)}. Try again in a moment.`);
    } finally {
      setExporting(false);
    }
  }, [api, filters]);

  const total = data?.total ?? 0;

  return (
    <Card
      title="Sessions"
      subtitle={
        includeNonSession
          ? 'Conversations and back-office work. The latter has no duration and no per-minute figure.'
          : 'One row per conversation. Back-office work (agent creation, ingest) is excluded.'
      }
      right={
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-1.5 text-[11px] text-gray-600">
            <input
              type="checkbox"
              checked={includeNonSession}
              onChange={(e) => setIncludeNonSession(e.target.checked)}
              className="rounded border-gray-300"
            />
            Show non-session work
          </label>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value as UsageSessionSort)}
            className="rounded border-gray-300 py-1 text-xs"
          >
            {SORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <button
            onClick={exportCsv}
            disabled={exporting}
            className="inline-flex items-center gap-1 rounded border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            title="Exactly the rows this filter selects — the same query, not a re-derivation"
          >
            <Download className="h-3 w-3" />
            {exporting ? 'Exporting…' : 'CSV'}
          </button>
        </div>
      }
      bodyClassName=""
    >
      {error && (
        <div className="border-b border-rose-200 bg-rose-50 px-4 py-2 text-xs text-rose-700">{error}</div>
      )}

      <Table
        head={
          <tr>
            <Th className="w-6" />
            <Th>Session</Th>
            <Th>Channel</Th>
            <Th align="right">Duration</Th>
            <Th align="right" title="LLM · speech out · speech in · carrier">Cost split</Th>
            <Th align="right" title="LLM turns, cost per turn, prompt tokens per turn">Turns</Th>
            <Th align="right">Cost</Th>
            <Th align="right" title="Cost divided by measured time. Blank when there is no meter.">{symbol}/min</Th>
            <Th align="right" title="What the rate card bills for this session">Billed</Th>
          </tr>
        }
      >
        {(data?.rows ?? []).map((r) => (
          <SessionRow
            key={rowId(r)}
            row={r}
            api={api}
            expanded={expanded === rowId(r)}
            onToggle={() => setExpanded(expanded === rowId(r) ? null : rowId(r))}
          />
        ))}
        {!loading && !data?.rows.length && (
          <Empty colSpan={9}>
            {includeNonSession
              ? 'No sessions or back-office work in this window. Widen the range or clear a filter.'
              : 'No sessions in this window. Widen the range, clear a filter, or tick “Show non-session work”.'}
          </Empty>
        )}
        {loading && !data?.rows.length && <Empty colSpan={9}>Loading…</Empty>}
      </Table>

      {total > PAGE && (
        <div className="flex items-center justify-between border-t border-gray-100 px-4 py-2 text-xs text-gray-600">
          <span className="tabular-nums">
            {offset + 1}–{Math.min(offset + PAGE, total)} of {int(total)}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setOffset(Math.max(0, offset - PAGE))}
              disabled={offset === 0}
              className="rounded border border-gray-300 px-2 py-1 disabled:opacity-40"
            >
              Previous
            </button>
            <button
              onClick={() => setOffset(offset + PAGE)}
              disabled={offset + PAGE >= total}
              className="rounded border border-gray-300 px-2 py-1 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </Card>
  );
};

const SessionRow: React.FC<{
  row: UsageSessionRow;
  api: AdminAPI;
  expanded: boolean;
  onToggle: () => void;
}> = ({ row, api, expanded, onToggle }) => {
  const [detail, setDetail] = useState<UsageSessionEvents | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);

  useEffect(() => {
    if (expanded && !detail && !detailError) {
      api.getUsageSessionEvents(row.session_key)
        .then(setDetail)
        .catch((e) => setDetailError(e instanceof Error ? e.message : String(e)));
    }
  }, [expanded, detail, detailError, api, row.session_key]);

  return (
    <>
      <tr className={`cursor-pointer hover:bg-gray-50 ${expanded ? 'bg-blue-50/40' : ''}`} onClick={onToggle}>
        <Td>{expanded ? <ChevronDown className="h-3.5 w-3.5 text-gray-400" /> : <ChevronRight className="h-3.5 w-3.5 text-gray-400" />}</Td>
        <Td>
          <div className="flex items-center gap-1.5">
            <span className="font-medium text-gray-900">{row.agent_name}</span>
            {row.direction && (
              <span className="inline-flex items-center gap-0.5 text-[10px] text-gray-500">
                <Phone className="h-2.5 w-2.5" />{row.direction}
              </span>
            )}
            {row.any_error && <span className="text-[10px] font-medium text-rose-600">error</span>}
          </div>
          <div className="text-[11px] text-gray-500">
            {row.organization_name || '—'} · {dateTime(row.started_at)} · {int(row.calls)} calls
          </div>
        </Td>
        <Td>
          <div className="flex items-center gap-1">
            <Chip kind="channel" value={row.channel} />
            {row.simulated && (
              <span
                className="rounded border border-violet-200 bg-violet-50 px-1.5 py-0.5 text-[10px] font-medium text-violet-700"
                title="A simulator run: real pipeline turns, no customer, no audio, no carrier minutes"
              >
                Simulated
              </span>
            )}
            {/* Messaging is not one channel — WhatsApp and SMS bill and behave
                differently, so the wire is shown, not just the category. */}
            {row.transport && <Chip kind="transport" value={row.transport} />}
          </div>
        </Td>
        <Td align="right" className="whitespace-nowrap">
          <div className="tabular-nums">{duration(row.duration_seconds)}</div>
          <div className="mt-0.5"><DurationBadge source={row.duration_source} /></div>
        </Td>
        <Td align="right" className="whitespace-nowrap text-[11px] tabular-nums">
          <CostSplit row={row} />
        </Td>
        <Td align="right" className="whitespace-nowrap tabular-nums">
          <div>{int(row.turns)}</div>
          <PerTurn row={row} />
        </Td>
        <Td align="right" className="tabular-nums font-medium text-gray-900"><Money value={row.cost_usd} inr={row.cost_inr} /></Td>
        <Td align="right" className="tabular-nums">
          <Money value={row.cost_per_minute} inr={row.cost_per_minute_inr} />
          {row.cost_per_minute != null && row.minutes ? (
            <div
              className="mt-0.5 whitespace-nowrap text-[10px] text-gray-400"
              title="The same all-in rate, split by what it is made of"
            >
              <PerMinute row={row} />
            </div>
          ) : null}
        </Td>
        <Td align="right" className="tabular-nums">
          <div>{row.credits_billable == null ? '—' : `${row.credits_billable} cr`}</div>
          <div className={`text-[10px] ${row.margin_usd < 0 ? 'text-rose-600' : 'text-gray-500'}`}>
            <Money value={row.revenue_usd} bare /> · {row.margin_usd >= 0 ? '+' : ''}<Money value={row.margin_usd} bare />
          </div>
        </Td>
      </tr>

      {expanded && (
        <tr className="bg-gray-50/70">
          <td colSpan={9} className="px-6 py-3">
            {detailError ? (
              <div className="py-2 text-xs text-rose-700">
                Couldn't load this session's events: {detailError}.{' '}
                <button onClick={(e) => { e.stopPropagation(); setDetailError(null); }} className="underline">Try again</button>
              </div>
            ) : (
              <SessionDetail row={row} detail={detail} />
            )}
          </td>
        </tr>
      )}
    </>
  );
};

const SessionDetail: React.FC<{ row: UsageSessionRow; detail: UsageSessionEvents | null }> = ({ row, detail }) => {
  if (!detail) return <div className="py-4 text-center text-xs text-gray-400">Loading events…</div>;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-x-6 gap-y-1 text-[11px] text-gray-600">
        <span><span className="text-gray-400">Session</span> <code className="text-gray-700">{row.session_key}</code></span>
        {row.per_session_credits != null && (
          <span title="The rate card that resolved for this session">
            <span className="text-gray-400">Rate card</span>{' '}
            {row.per_session_credits} cr/session
            {row.per_minute_credits ? ` + ${row.per_minute_credits} cr/min` : ''}
            {row.minimum_minutes ? `, ${row.minimum_minutes} min floor` : ''}
            {row.rounding_seconds ? `, ${row.rounding_seconds}s increments` : ''}
          </span>
        )}
      </div>

      {row.overhead_usd != null && row.overhead_usd > 0 && (
        <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] text-slate-700" title="Analysis and compliance that ran on this thread after the conversation; charged to it once, and not in the per-minute rate">
          <strong>Analysis afterwards</strong> · <Money value={row.overhead_usd} inr={row.overhead_inr} bare /> ·
          all-in with it: <strong><Money value={row.cost_usd + row.overhead_usd} inr={row.cost_inr != null ? row.cost_inr + (row.overhead_inr ?? 0) : null} bare /></strong>
        </div>
      )}
      {detail.call && (
        <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-900">
          <strong>Carrier leg</strong> · {capitalise(detail.call.provider)} {detail.call.direction} ·{' '}
          {detail.call.status}{detail.call.hangup_reason ? ` (${detail.call.hangup_reason})` : ''} ·{' '}
          {duration(detail.call.duration_seconds)} talk
          {detail.call.billed_seconds != null && <> → {duration(detail.call.billed_seconds)} billed</>}
          {detail.call.rate_per_minute != null && <> @ {rate(detail.call.rate_per_minute, '/min', symbolFor(detail.call.currency))}</>}
          {detail.call.cost_usd != null && <> = <strong><Money value={detail.call.cost_usd} bare /></strong></>}
          {detail.call.cost_usd == null && (
            <> — <span className="font-medium">not costed</span>; calls are priced at hangup.</>
          )}
        </div>
      )}

      <div className="overflow-hidden rounded border border-gray-200 bg-white">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <Th>Time</Th>
              <Th>Type</Th>
              <Th>Model</Th>
              <Th>Function</Th>
              <Th align="right">Usage</Th>
              <Th align="right">Latency</Th>
              <Th align="right">Cost</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {detail.events.map((e) => (
              <tr key={e.id} className={e.status !== 'success' ? 'bg-rose-50/50' : ''}>
                <Td className="whitespace-nowrap text-[11px] tabular-nums text-gray-500">
                  {e.created_at.slice(11, 19)}
                </Td>
                <Td><Chip kind="modality" value={e.modality} /></Td>
                <Td className="text-[11px]">
                  <div className="text-gray-800">{e.model}</div>
                  <div className="text-gray-400">{e.provider}</div>
                </Td>
                <Td className="text-[11px] text-gray-500">{e.baml_function || e.feature}</Td>
                <Td align="right" className="whitespace-nowrap text-[11px] tabular-nums text-gray-600">
                  {eventUsage(e)}
                </Td>
                <Td align="right" className="text-[11px] tabular-nums text-gray-500">
                  {e.duration_ms == null ? '—' : `${int(e.duration_ms)}ms`}
                </Td>
                <Td align="right" className="tabular-nums font-medium text-gray-900"><Money value={e.cost_usd} bare /></Td>
              </tr>
            ))}
            {!detail.events.length && <Empty colSpan={7}>No priced events on this session.</Empty>}
          </tbody>
          <tfoot className="bg-gray-50">
            <tr>
              <Td className="text-[11px] font-medium text-gray-600">
                {int(detail.totals.calls)} events
              </Td>
              <td colSpan={5} />
              <Td align="right" className="tabular-nums font-semibold text-gray-900">
                <Money value={detail.totals.cost_usd} bare />
              </Td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

const eventUsage = (e: UsageSessionEvents['events'][number]): string => {
  if (e.modality === 'tts') return `${compact(e.char_count ?? 0)} chars`;
  if (e.modality === 'stt') return `${(e.audio_seconds ?? 0).toFixed(1)}s audio`;
  if (e.modality === 'telephony') return `${duration(e.audio_seconds ?? 0)} billed`;
  return `${compact(e.input_tokens)} → ${compact(e.output_tokens)} tok`;
};

export const MODALITY_NAMES = MODALITY_LABEL;


/** The split, in the page currency. Four decimals: read as a proportion. */
const CostSplit: React.FC<{ row: UsageSessionRow }> = ({ row }) => {
  const { fmt } = useCurrency();
  return (
    <>
      <span className="text-blue-600">{fmt(row.llm_cost, undefined, 4)}</span>
      <span className="text-gray-300"> · </span>
      <span className="text-pink-600">{fmt(row.tts_cost, undefined, 4)}</span>
      <span className="text-gray-300"> · </span>
      <span className="text-teal-600">{fmt(row.stt_cost, undefined, 4)}</span>
      {row.telephony_cost > 0 && (
        <>
          <span className="text-gray-300"> · </span>
          <span className="text-amber-600">{fmt(row.telephony_cost, undefined, 4)}</span>
        </>
      )}
    </>
  );
};

const PerMinute: React.FC<{ row: UsageSessionRow }> = ({ row }) => {
  const { fmt } = useCurrency();
  const m = row.minutes || 1;
  return (
    <>
      LLM {fmt(row.llm_cost / m, undefined, 4)} · TTS {fmt(row.tts_cost / m, undefined, 4)}
      {row.stt_cost > 0 && <> · STT {fmt(row.stt_cost / m, undefined, 4)}</>}
      {row.telephony_cost > 0 && <> · carrier {fmt(row.telephony_cost / m, undefined, 4)}</>}
    </>
  );
};

const PerTurn: React.FC<{ row: UsageSessionRow }> = ({ row }) => {
  const { fmt } = useCurrency();
  if (!row.turns) return null;
  return (
    <div className="text-[10px] text-gray-400" title="Cost per LLM turn · prompt tokens per turn">
      {fmt(row.cost_per_turn, undefined, 4)}/turn{row.tokens_per_turn != null && <> · {compact(row.tokens_per_turn)} tok</>}
    </div>
  );
};
